/**
 * TruWe diagnostic quiz routing.
 * Pure, deterministic, easy to unit test.
 */

export type PrimaryGoal =
  | "energy"
  | "hormones"
  | "liver"
  | "sleep"
  | "multiple";

export type Symptom =
  | "fatigue"
  | "poorSleep"
  | "hormonalAcne"
  | "bloating"
  | "fogMood"
  | "crampsRecovery";

export type SKU = "mag-absorb-pro" | "liver-biome-plus" | "ova-balance";

export type Recommendation = {
  primary: SKU;
  supporting: SKU | null;
  /** True when the answers suggest a full protocol bundle. */
  bundle: boolean;
  /** Internal scores for debugging / unit tests. */
  scores: Record<SKU, number>;
};

const GOAL_WEIGHTS: Record<PrimaryGoal, Partial<Record<SKU, number>>> = {
  energy: { "mag-absorb-pro": 2, "liver-biome-plus": 2 },
  hormones: { "ova-balance": 3 },
  liver: { "liver-biome-plus": 3 },
  sleep: { "mag-absorb-pro": 3 },
  multiple: {},
};

const SYMPTOM_WEIGHTS: Record<Symptom, Partial<Record<SKU, number>>> = {
  fatigue: { "liver-biome-plus": 1, "mag-absorb-pro": 1 },
  poorSleep: { "mag-absorb-pro": 2 },
  hormonalAcne: { "ova-balance": 2, "liver-biome-plus": 1 },
  bloating: { "liver-biome-plus": 2 },
  fogMood: { "mag-absorb-pro": 1, "ova-balance": 1 },
  crampsRecovery: { "mag-absorb-pro": 2 },
};

export function recommendProtocol(
  primaryGoal: PrimaryGoal,
  symptoms: Symptom[],
): Recommendation {
  const scores: Record<SKU, number> = {
    "mag-absorb-pro": 0,
    "liver-biome-plus": 0,
    "ova-balance": 0,
  };

  const goalWeights = GOAL_WEIGHTS[primaryGoal];
  for (const [sku, w] of Object.entries(goalWeights)) {
    scores[sku as SKU] += w ?? 0;
  }
  for (const s of symptoms) {
    const w = SYMPTOM_WEIGHTS[s];
    for (const [sku, n] of Object.entries(w)) {
      scores[sku as SKU] += n ?? 0;
    }
  }

  // Fallback if nothing scored — default to mag (most universally relevant).
  const total = scores["mag-absorb-pro"] + scores["liver-biome-plus"] + scores["ova-balance"];
  if (total === 0) {
    return {
      primary: "mag-absorb-pro",
      supporting: null,
      bundle: primaryGoal === "multiple",
      scores,
    };
  }

  const sorted = (Object.keys(scores) as SKU[]).sort((a, b) => scores[b] - scores[a]);
  const primary = sorted[0];
  const secondScore = scores[sorted[1]];
  const primaryScore = scores[primary];

  // Supporting product if the runner-up is within 60% of primary's score.
  const supporting =
    secondScore > 0 && secondScore / primaryScore >= 0.6 ? sorted[1] : null;

  return {
    primary,
    supporting,
    bundle: primaryGoal === "multiple" || supporting !== null,
    scores,
  };
}

export const SKU_META: Record<
  SKU,
  {
    name: string;
    tagline: string;
    why: string;
    timeline: { day7: string; day15: string; day30: string };
  }
> = {
  "mag-absorb-pro": {
    name: "Mag Absorb Pro",
    tagline: "5-form magnesium for sleep, calm & recovery",
    why: "Magnesium is the cofactor your nervous system, muscles and sleep architecture all run on. The 5-form matrix here anchored by liposomal bisglycinate and Aquamin® marine magnesium, with chloride, aspartate and gluconate for staged uptake pairs with zinc, copper, P-5-P and D3 cofactors so your body can actually use it.",
    timeline: {
      day7: "Calmer evenings, less mid-night waking.",
      day15: "Deeper sleep, fewer cramps, steadier energy on waking.",
      day30: "Noticeably better recovery and a quieter baseline stress response.",
    },
  },
  "liver-biome-plus": {
    name: "Liver Biome Plus",
    tagline: "Liposomal liver detox + multi-strain probiotic",
    why: "Liver Biome Plus supplies the exact cofactors your liver's Phase I and Phase II pathways run on, formulated with liposomal carriers and Bio-Absorb Matrix to support delivery of silymarin, curcumin and ALA.",
    timeline: {
      day7: "Lighter mornings, less post-meal bloat.",
      day15: "Smoother digestion, steadier energy through the day.",
      day30: "Clearer skin, regular bowels, and a measurable drop in daily fatigue.",
    },
  },
  "ova-balance": {
    name: "Ova Balance",
    tagline: "Myo + D-Chiro 40:1 for cycles, mood & metabolic balance",
    why: "Ova Balance uses the same 40:1 myo-to-D-chiro inositol ratio validated in published PCOS trials, plus Berberine HCl with Bio-Absorb Matrix to support insulin sensitivity, the upstream lever for cycles, skin and weight.",
    timeline: {
      day7: "Fewer sugar cravings, steadier mood through the day.",
      day15: "Clearer skin, less PMS-driven irritability.",
      day30: "More regular cycles and easier metabolic baseline, most users see clearer change over 3 cycles.",
    },
  },
};
